import type { ContentBlock } from '../hooks/useClaudeSession'

/** Strip Markdown syntax so text-to-speech reads natural prose instead of
 *  pronouncing "asterisk asterisk", hash marks, backticks, link URLs, etc.
 *  This is intentionally lightweight (regex-based) — it favours readable
 *  speech over perfect Markdown parsing. */
export function stripMarkdownForSpeech(md: string): string {
  let text = md

  // Fenced code blocks — drop entirely (reading code aloud is noise).
  text = text.replace(/```[\s\S]*?```/g, ' ')
  // Inline code — keep the contents, drop the backticks.
  text = text.replace(/`([^`]+)`/g, '$1')
  // Images — drop (don't read URLs/alt as speech).
  text = text.replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
  // Links — keep the visible text, drop the URL.
  text = text.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
  // Headings — drop leading hashes.
  text = text.replace(/^[ \t]*#{1,6}[ \t]+/gm, '')
  // Blockquote markers.
  text = text.replace(/^[ \t]*>[ \t]?/gm, '')
  // Horizontal rules (before list/emphasis handling).
  text = text.replace(/^[ \t]*([-*_])(?:[ \t]*\1){2,}[ \t]*$/gm, ' ')
  // Unordered list markers (-, *, +) at line start.
  text = text.replace(/^[ \t]*[-*+][ \t]+/gm, '')
  // Ordered list markers (1. 2. …).
  text = text.replace(/^[ \t]*\d+\.[ \t]+/gm, '')
  // Bold / italic / strikethrough — keep inner text, drop the markers.
  text = text.replace(/(\*\*|__)(.+?)\1/g, '$2')
  text = text.replace(/(\*|_)(.+?)\1/g, '$2')
  text = text.replace(/~~(.+?)~~/g, '$1')
  // Runs of dashes/equals (table delimiter rows like |---|:--:|, horizontal
  // rules, ===) — collapse so they aren't read out one character at a time.
  text = text.replace(/[-=–—]{2,}/g, ' ')
  // Table pipe separators.
  text = text.replace(/\|/g, ' ')
  // Any stray HTML tags.
  text = text.replace(/<[^>]+>/g, '')
  // Leftover emphasis/code characters.
  text = text.replace(/[*_`]+/g, '')
  // Collapse whitespace.
  text = text.replace(/[ \t]+/g, ' ')
  text = text.replace(/ *\n */g, '\n')
  text = text.replace(/\n{3,}/g, '\n\n')

  return text.trim()
}

/** Split text into sentence-aligned chunks of at most `maxLen` characters.
 *  Neural TTS synthesizes the whole input before returning, so a long reply
 *  takes many seconds. Chunking lets the player synthesize + play the first
 *  chunk quickly and stream the rest, keeping time-to-first-audio low.
 *
 *  `firstMaxLen` caps the FIRST chunk (defaults to `maxLen`). Pass a small value
 *  (~1 sentence) so the opening chunk synthesizes near-instantly — that is what
 *  makes "Listen" start reading right away. From there the cap RAMPS UP (roughly
 *  doubling per chunk) toward `maxLen`: the early chunks stay small so each is
 *  ready before the previous one finishes playing (no mid-reply silence while a
 *  full-size chunk is still synthesizing), then chunks grow to keep the request
 *  count down once the pipeline has built a lead. Internal whitespace/newlines
 *  in each chunk are collapsed to single spaces so TTS doesn't over-pause. */
export function splitIntoSpeechChunks(text: string, maxLen = 480, firstMaxLen = maxLen): string[] {
  const clean = text.trim()
  if (!clean) return []

  // Keep sentence-ending punctuation and treat blank lines as breaks.
  const pieces = clean.match(/[^.!?\n]+[.!?]*|\n+/g) ?? [clean]
  const chunks: string[] = []
  let current = ''

  // Collapse internal whitespace (incl. newlines/paragraph breaks) to single
  // spaces before a chunk is emitted. Neural TTS can render a bare "\n\n" as a
  // multi-second pause; sentence-ending punctuation already carries the natural
  // pauses, so this keeps playback flowing instead of stalling between lines.
  const push = (s: string) => {
    const normalized = s.trim().replace(/\s+/g, ' ')
    if (normalized) chunks.push(normalized)
  }

  // Packing threshold ramps UP: the first chunk is small (fast first audio),
  // then chunks grow toward maxLen. A small second/third chunk synthesizes
  // quickly, so it is ready before the short opener finishes playing — that is
  // what stops the ~5s silence gap after the first sentence (the opener is short
  // but the next full-size chunk was too slow to synthesize in time). Once the
  // pipeline has a lead, larger chunks keep the request count down.
  const cap = () => Math.min(maxLen, firstMaxLen * 2 ** chunks.length)

  const flush = () => {
    push(current)
    current = ''
  }

  for (const piece of pieces) {
    if (piece.length > maxLen) {
      // A single sentence longer than maxLen — hard-split on length.
      flush()
      for (let i = 0; i < piece.length; i += maxLen) {
        push(piece.slice(i, i + maxLen))
      }
      continue
    }
    // Fast first audio: when the reply OPENS with one long sentence (longer than
    // firstMaxLen but under maxLen, so the hard-split above didn't fire), break
    // it at a clause boundary so the opener still synthesizes near-instantly.
    // We only ever cut at real punctuation (comma/semicolon/colon/dash), never
    // mid-word — a sentence with no such boundary is left whole.
    if (chunks.length === 0 && !current && piece.length > firstMaxLen) {
      const cut = firstChunkClauseCut(piece, firstMaxLen, maxLen)
      if (cut > 0) {
        push(piece.slice(0, cut))
        current = piece.slice(cut)
        continue
      }
    }
    if (current && current.length + piece.length > cap()) flush()
    current += piece
  }
  flush()

  return chunks
}

/** For the opening chunk: find where to break an over-long first sentence at a
 *  clause boundary (", " "; " ": " "— ") so the opener stays small and fast to
 *  synthesize. Prefers the LATEST boundary that still fits under `firstMaxLen`
 *  (pack as much as fits); if none fits, falls back to the EARLIEST boundary
 *  under `maxLen` (better a slightly larger opener than none). Returns the cut
 *  index (just past the punctuation) or 0 when there is no boundary to cut at —
 *  in which case the caller keeps the sentence whole rather than split a word. */
function firstChunkClauseCut(piece: string, firstMaxLen: number, maxLen: number): number {
  const boundary = /[,;:—–]\s/g
  let best = 0        // latest boundary end at/under firstMaxLen
  let firstBeyond = 0 // earliest boundary end over firstMaxLen but under maxLen
  let m: RegExpExecArray | null
  while ((m = boundary.exec(piece)) !== null) {
    const end = m.index + 1  // include the punctuation, exclude the space
    if (end <= firstMaxLen) best = end
    else if (firstBeyond === 0 && end <= maxLen) firstBeyond = end
  }
  return best || firstBeyond
}

/** Collect the raw (Markdown-preserving) text of an assistant message's
 *  content blocks — used by the Copy button so pasted text keeps its
 *  formatting. Tool calls, tool results and thinking blocks are excluded. */
export function extractCopyText(content: ContentBlock[]): string {
  return content
    .filter(b => b.type === 'text' && b.text)
    .map(b => b.text as string)
    .join('\n\n')
    .trim()
}

/** Collect the spoken text from an assistant message's content blocks.
 *  Only plain `text` blocks are spoken — tool calls, tool results and
 *  thinking blocks are excluded — and Markdown syntax is stripped so the
 *  reading sounds natural. */
export function extractSpeakableText(content: ContentBlock[]): string {
  const raw = content
    .filter(b => b.type === 'text' && b.text)
    .map(b => b.text as string)
    .join('\n\n')
  return stripMarkdownForSpeech(raw).trim()
}
